import Vue from "vue";
import axios from "axios";
import qs from "qs";
import "cookieconsent";
import uriJs from "urijs";
import { dom } from "@fortawesome/fontawesome-svg-core";
import LazyLoad from "vanilla-lazyload/dist/lazyload";
import WebFont from "webfontloader";
import {
  FontAwesomeIcon,
  FontAwesomeLayers
} from "@fortawesome/vue-fontawesome";
import * as Sentry from "@sentry/browser";
import * as Integrations from "@sentry/integrations";
import { Integrations as VueIntegrations } from "@sentry/tracing";
import { VLazyImagePlugin } from "v-lazy-image";
import VueCtkDateTimePicker from "vue-ctk-date-time-picker";
import { VueReCaptcha } from "vue-recaptcha-v3";
import { store } from "~/store";
import "fg-loadcss/dist/cssrelpreload";
import Cookie from "~/modules/cookie";
import "~/modules/fontAwesome";
import "../Styles/templates/landingPageItem.less";
import ContactCollection from "#/LandingPages/ContactCollection.vue";
import RadioListField from "#/LandingPages/Fields/RadioListField.vue";
import CheckboxListField from "#/LandingPages/Fields/CheckboxListField.vue";
import DropdownField from "#/LandingPages/Fields/DropdownField.vue";
import TextboxField from "#/LandingPages/Fields/TextboxField.vue";
import LinkField from "#/LandingPages/Fields/LinkField.vue";
import CheckboxField from "#/LandingPages/Fields/CheckboxField.vue";
import TextareaField from "#/LandingPages/Fields/TextareaField.vue";
import DatePickerField from "#/LandingPages/Fields/DatePickerField.vue";
import DateTimePickerField from "#/LandingPages/Fields/DateTimePickerField.vue";
import CurrencyField from "#/LandingPages/Fields/CurrencyField.vue";
import MatrixField from "#/LandingPages/Fields/MatrixField.vue";
import PasswordField from "#/LandingPages/Fields/PasswordField.vue";
import "iframe-resizer/js/iframeResizer.contentWindow.min";
import iframeResize from 'iframe-resizer/js/iframeResizer';

// #region CSS

// Require critical styles if not in production,
// as the template will only inline them in production.
/* eslint-disable global-require */
if ( process.env.NODE_ENV !== "production" ) {
  require( "../Styles/critical/landingPageItem.less" );
} else {
  const environment = document.querySelector( "meta[name=\"environment\"]" ).getAttribute( "content" );
  Vue.prototype.$environment = environment;

  const version = document.querySelector( "meta[name=\"version\"]" ).getAttribute( "content" );
  Vue.prototype.$version = version;

  const packageString = document.querySelector( "meta[name=\"package\"]" ).getAttribute( "content" );
  Vue.prototype.$package = packageString;

  const evdt = document.querySelector( "meta[name=\"evdt\"]" ).getAttribute( "content" ) === "true";
  if ( evdt ) {
    Vue.config.devtools = true;
  }

  Sentry.init( {
    dsn: "https://5d53717ed7404c9890ecb112c83f3edb@o351484.ingest.sentry.io/5496363",
    environment,
    release: `${packageString}@${version}`,
    integrations: [
      new VueIntegrations.BrowserTracing(),
      new Integrations.Vue( {
        Vue,
        attachProps: true,
        logErrors: true,
        tracing: true,
        tracingOptions: {
          trackComponents: true
        }
      } )
    ],
    denyUrls: [
      // Facebook flakiness
      /graph\.facebook\.com/i,
      // Facebook blocked
      /connect\.facebook\.net\/en_US\/all\.js/i,
      // Woopra flakiness
      /eatdifferent\.com\.woopra-ns\.com/i,
      /static\.woopra\.com\/js\/woopra\.js/i,
      // Chrome extensions
      /extensions\//i,
      /^chrome:\/\//i,
      // Other plugins
      /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
      /webappstoolbarba\.texthelp\.com\//i,
      /metrics\.itunes\.apple\.com\.edgesuite\.net\//i
    ],
    tracesSampleRate: 0.33
  } );

  // LogRocket.getSessionURL( sessionURL => {
  //  Sentry.configureScope( scope => {
  //    scope.setExtra( "sessionURL", sessionURL );
  //  } );
  // } );
}
/* eslint-enable global-require */

// #endregion

// #region Web fonts

WebFont.load( {
  google: {
    families: [
      "Ubuntu:400,500",
      "Overpass:400,600"
    ]
  }
} );

// #endregion

// #region FontAwesome

dom.watch();

// #endregion

// #region Vue

Vue.directive('resize', {
  bind: function(el, { value = {} }) {
    el.addEventListener('load', () => iframeResize(value, el))
  },
  unbind: function (el) {
    el.iFrameResizer.removeListeners();
  }
})

// Ensure we have access to our CSRF token
const csrfToken = document.querySelector( "meta[name=\"csrf-token\"]" ).getAttribute( "content" );
Vue.prototype.$csrfToken = csrfToken;

// Ensure we have access to our Umbraco node ID
const nodeId = document.querySelector( "meta[name=\"node-id\"]" ).getAttribute( "content" );
Vue.prototype.$nodeId = nodeId;

// Register any plugins
Vue.use( VLazyImagePlugin );

// For more options see below
const siteKey = document.querySelector( "meta[name=\"siteKey\"]" ).getAttribute( "content" );
Vue.use( VueReCaptcha, { siteKey } );

const evdt = document.querySelector( "meta[name=\"evdt\"]" ).getAttribute( "content" ) === "true";
if ( evdt ) {
  Vue.config.devtools = true;
}

// Register any global components
Vue.component( "FontAwesomeIcon", FontAwesomeIcon );
Vue.component( "FontAwesomeLayers", FontAwesomeLayers );
Vue.component( "VueCtkDateTimePicker", VueCtkDateTimePicker );

// Create our instance
new Vue( {
  el: "#app",
  store,
  components: {
    ContactCollection,
    CheckboxListField,
    DropdownField,
    TextboxField,
    LinkField,
    RadioListField,
    CheckboxField,
    TextareaField,
    DatePickerField,
    DateTimePickerField,
    CurrencyField,
    MatrixField,
    PasswordField
  },
  data: () => ( {
    submitting: false,
    components: [],
    showValidation: false,
    errorMessage: null,
    removeErrorOnValid: false,
    formData: {
      orgId: null,
      personId: null,
      email: null,
      orgName: null,
      address1: null,
      address2: null,
      address3: null,
      townCity: null,
      region: null,
      postCode: null,
      countryId: null,
      title: null,
      firstName: null,
      lastName: null,
      fullName: null,
      jobTitle: null,
      telephone: null,
      jobFunctionId: null,
      fieldValues: [],
      createWebAccount: false,
      useJobFunction: false,
      webAccountTypeId: null,
      webUserTypeId: null,
      webAccountID: null,
      crmActivityId: null,
      eventDelegateId: null
    }
  } ),
  computed: {
    isValid () {
      let result = true;

      if ( this.components && this.components.length > 0 ) {
        result = this.components.every( x => x.valid );
      }

      return result;
    }
  },
  watch: {
    isValid ( val ) {
      if ( this.removeErrorOnValid && val ) {
        this.errorMessage = null;
      }
    }
  },
  created () {
    const urlObj = uriJs( window.location.href ).query( true );
    let dataKey = urlObj.K;
    let { personId } = urlObj;
    let { orgId } = urlObj;

    if ( !dataKey ) {
      dataKey = urlObj.k;
    }

    if ( !personId ) {
      personId = urlObj.personid;
    }

    if ( !orgId ) {
      orgId = urlObj.orgid;
    }

    if ( ( dataKey && dataKey.length > 0 ) || ( personId && personId.length > 0 )
     || ( orgId && orgId.length > 0 ) ) {
      const formData = {
        __RequestVerificationToken: this.$csrfToken,
        dataKey,
        personId,
        orgId
      };

      const url = "/umbraco/Surface/LandingPageItemSurface/GetLandingPageData";

      axios.post( url, qs.stringify( formData ) )
        .then( response => {
          if ( response.data ) {
            // Populate the form data with existing data
            this.formData = response.data;
          } else {
            throw Error( "Server indicates that the submission load failed" );
          }
        } )
        .catch( error => {
          this.errorMessage = "An error occurred whilst loading the form data. Please try again later.";
          console.error( error );
        } );
    }
  },
  methods: {
    validateComponent ( val ) {
      if ( val ) {
        const match = this.components.find( x => x.alias === val.alias );

        if ( !match ) {
          this.components.push( val );
        } else {
          const index = this.components.indexOf( match );
          this.components[index].valid = val.valid;
        }
      }
    },
    async getRecaptchaToken () {
      // (optional) Wait until recaptcha has been loaded.
      await this.$recaptchaLoaded();

      return this.$recaptcha( "submit" );
    },
    async submit ( redirectUrl ) {
      const token = await this.getRecaptchaToken();

      const url = "/umbraco/Surface/LandingPageItemSurface/Submit";
      this.showValidation = true;

      if ( this.isValid ) {
        if ( !this.submitting ) {
          this.submitting = true;

          const formData = {
            __RequestVerificationToken: this.$csrfToken,
            "g-recaptcha-response": token,
            nodeId: this.$nodeId,
            ...this.formData,
            initialUrl: window.location.href
          };

          axios.post( url, qs.stringify( formData ) )
            .then( response => {
              if ( response.data ) {
                /* Uncomment to re-enable iframe parent messaging */
                // this.messageParent( "success", this.formData );
                if ( window.__LP_CONFIG__.redirectTopWindow ) {
                  window.top.location.href = redirectUrl;
                } else {
                  window.document.location = redirectUrl;
                }
              } else {
                /* Uncomment to re-enable iframe parent messaging */
                // this.messageParent( "error", 101 );
                throw Error( "Server indicates that the submission failed" );
              }
            } )
            .catch( error => {
              /* Uncomment to re-enable iframe parent messaging */
              // this.messageParent( "error", 102 );
              this.errorMessage = "An error occurred whilst submitting the form. Please try again later.";
              console.error( error );
            } )
            .finally( () => {
              this.submitting = false;
            } );
        }
      } else {
        this.submitting = false;
        this.removeErrorOnValid = true;
        this.errorMessage = "Please fill out of all the required fields and try again.";
      }
    },
    messageParent ( event, payload ) {
      window.parent.postMessage( {
        event,
        payload
      }, "*" /* This needs replacing with a specified origin */ );
    }
  }
} );

// #endregion

// #region Lazyloading

// Enable image lazyloading
new LazyLoad( {
  elements_selector: "img, .lazy"
} );

// #endregion

// #region Cookies

document.addEventListener( "DOMContentLoaded", () => {
  let isEmbedded = false;
  const isEmbeddedCntrl = document.getElementById( "isEmbedded" );
  if ( isEmbeddedCntrl && isEmbeddedCntrl.value === "true" ) {
    isEmbedded = true;
  }

  if ( !isEmbedded ) {
    const content = {
      message: `We'd like your permission to store cookies to help us understand
      how visitors are using our website so we can improve the experience for them.`,
      deny: "No thanks",
      allow: "That's ok",
      href: "/cookies/"
    };

    /* eslint-disable no-underscore-dangle */
    if ( window.__COOKIE_CONSENT__ && Object.keys( window.__COOKIE_CONSENT__ ).length !== 0 ) {
      if ( window.__COOKIE_CONSENT__.allow ) {
        content.allow = window.__COOKIE_CONSENT__.allow;
      }

      if ( window.__COOKIE_CONSENT__.deny ) {
        content.deny = window.__COOKIE_CONSENT__.deny;
      }

      if ( window.__COOKIE_CONSENT__.message ) {
        content.message = window.__COOKIE_CONSENT__.message;
      }
    }
    /* eslint-enable no-underscore-dangle */

    window.cookieconsent.initialise( {
      position: "top",
      static: true,
      content,
      showLink: false,
      type: "opt-in",
      compliance: {
        "opt-in": "<div class=\"cc-compliance cc-highlight\">{{deny}}{{allow}}</div>"
      },
      onStatusChange () {
        const didConsent = this.hasConsented();
        if ( didConsent ) {
          Cookie.acceptCookies();
        }
        if ( !didConsent ) {
          Cookie.denyCookies();
        }

        const elem = document.querySelector( ".cc-grower" );
        if ( elem ) {
          elem.remove();
        }
      },
      onRevokeChoice () {
        const { type } = this.options;
        if ( type === "opt-in" ) {
          Cookie.acceptCookies();
        }
        if ( type === "opt-out" ) {
          Cookie.denyCookies();
        }

        const elem = document.querySelector( ".cc-grower" );
        if ( elem ) {
          elem.remove();
        }
      }
    } );
  }
} );

// #endregion
